import React, { useState, useEffect } from 'react';
import Styles from './styles.module.css';
import Loader from '../../assets/svg/loader.svg';

const CustomerSelector = ({ 
    customers, 
    selectedCustomer, 
    onCustomerSelect, 
    onCustomerSearch, 
    isLoading,
    salesChannels = []
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchTerm.length >= 3) {
                onCustomerSearch(searchTerm);
            } else if (searchTerm.length === 0) {
                // Only search if there's a selected customer to clear
                if (selectedCustomer) {
                    onCustomerSearch('');
                }
            }
        }, 300);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    const handleCustomerClick = (customer) => {
        onCustomerSelect(customer);
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        // Clear selected customer when search term changes
        if (selectedCustomer) {
            onCustomerSelect(null);
        }
    };

    return (
        <div className={Styles.container}>
            <div className={Styles.card}>
                <h3 className={Styles.title}>Kunde auswählen</h3>
                <div className={Styles.searchContainer}>
                    <input
                        type="text"
                        placeholder="Nach Kunde suchen (min. 3 Zeichen)..."
                        value={searchTerm}
                        onChange={handleInputChange}
                        className={Styles.searchInput}
                    />
                    {isLoading && (
                        <div className={Styles.loadingIndicator}>
                            <img src={Loader} width={20} height={20} alt="Loading" />
                        </div>
                    )}
                </div>

                {searchTerm.length >= 3 && (
                    <div className={Styles.searchResults}>
                        {isLoading ? (
                            <div className={Styles.loadingResults}>
                                <img src={Loader} width={20} height={20} alt="Loading" />
                                <span>Suche nach Kunden...</span>
                            </div>
                        ) : customers.length > 0 ? (
                            <>
                                <div className={Styles.resultsHeader}>
                                    <h4>{customers.length} Kunde{customers.length !== 1 ? 'n' : ''} gefunden</h4>
                                </div>
                                <div className={Styles.resultsTable}>
                                    <div className={Styles.tableHeader}>
                                        <span>Kundennummer</span>
                                        <span>Name</span>
                                        <span>Email</span>
                                        <span>Firma</span>
                                    </div>
                                    {customers.map((customer) => (
                                        <div
                                            key={customer.id}
                                            className={`${Styles.tableRow} ${
                                                selectedCustomer?.id === customer.id ? Styles.selected : ''
                                            }`}
                                            onClick={() => handleCustomerClick(customer)}
                                        >
                                            <span className={Styles.customerNumber}>
                                                {customer.customerNumber || 'N/A'}
                                            </span>
                                            <span className={Styles.customerName}>
                                                {customer.firstName} {customer.lastName}
                                            </span>
                                            <span className={Styles.customerEmail}>
                                                {customer.email}
                                            </span>
                                            <span className={Styles.customerCompany}>
                                                {customer.company || '-'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className={Styles.noResults}>
                                <p>Keine Kunden gefunden für "{searchTerm}"</p>
                                <p>Versuchen Sie eine andere Suche</p>
                            </div>
                        )}
                    </div>
                )}

                {selectedCustomer && (
                    <div className={Styles.selectedCustomer}>
                        <h4>Ausgewählter Kunde:</h4>
                        <div className={Styles.customerDetails}>
                            <div className={Styles.customerInfo}>
                                <p><strong>Name:</strong> {selectedCustomer.firstName} {selectedCustomer.lastName}</p>
                                <p><strong>Email:</strong> {selectedCustomer.email}</p>
                                <p><strong>Kundennummer:</strong> {selectedCustomer.customerNumber || 'N/A'}</p>
                                {selectedCustomer.company && (
                                    <p><strong>Firma:</strong> {selectedCustomer.company}</p>
                                )}
                                <p><strong>Kundengruppe:</strong> {
                                    selectedCustomer.group?.name || 
                                    selectedCustomer.group?.translated?.name ||
                                    (selectedCustomer.group ? `ID: ${selectedCustomer.group.id}` : '') ||
                                    (selectedCustomer.customerGroupId ? `Group ID: ${selectedCustomer.customerGroupId}` : '') ||
                                    'Nicht verfügbar'
                                }</p>
                                <p><strong>Kunden-Verkaufskanal:</strong> {
                                    selectedCustomer.salesChannelId 
                                        ? (salesChannels.find(sc => sc.id === selectedCustomer.salesChannelId)?.name || 
                                           `ID: ${selectedCustomer.salesChannelId}`)
                                        : 'Nicht verfügbar'
                                }</p>
                                {selectedCustomer.vatIds && selectedCustomer.vatIds.length > 0 && (
                                    <p><strong>USt-IdNr.:</strong> {selectedCustomer.vatIds.join(', ')}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerSelector;